<template>
  <div class="student-management">
    <nav class="student-breadcrumb" aria-label="页面位置"><router-link to="/home/index">首页</router-link><v-icon size="13">mdi-chevron-right</v-icon><span>教务中心</span><v-icon size="13">mdi-chevron-right</v-icon><span>学员管理</span></nav>
    <header class="student-page-heading">
      <div><h1>学员管理</h1><p>学员档案、分班与家长绑定，清晰掌握。</p></div>
      <div class="student-page-actions">
        <v-btn outlined class="student-secondary" @click="importStudent.show=true"><v-icon left size="17">mdi-tray-arrow-up</v-icon>批量导入</v-btn>
        <v-btn depressed color="#d9363e" dark @click="showAddDialog"><v-icon left size="18">mdi-account-plus-outline</v-icon>添加学员</v-btn>
      </div>
    </header>

    <section class="student-filter-panel" aria-label="学员筛选">
      <div class="student-filter-title"><h2>筛选学员</h2><span>快速找到你要跟进的学员</span></div>
      <form @submit.prevent="queryClicked">
        <div class="student-filters">
          <div><label for="student-name-filter">学员姓名</label><v-text-field id="student-name-filter" v-model="query.studentName" placeholder="请输入姓名" aria-label="学员姓名" prepend-inner-icon="mdi-account-outline" clearable dense outlined hide-details /></div>
          <div><label for="student-mobile-filter">手机号</label><v-text-field id="student-mobile-filter" v-model="query.mobile" placeholder="请输入手机号" aria-label="手机号" clearable dense outlined hide-details /></div>
          <div class="student-date-filter"><span class="student-field-label">创建时间</span><div class="student-date-range">
            <v-menu ref="menu1" v-model="menu1" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }"><v-text-field v-model="query.start" placeholder="开始日期" aria-label="开始日期" readonly v-bind="attrs" v-on="on" dense clearable outlined hide-details /></template>
              <v-date-picker v-model="query.start" @input="menu1=false" no-title scrollable locale="zh-cn" />
            </v-menu><span>至</span>
            <v-menu ref="menu2" v-model="menu2" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }"><v-text-field v-model="query.end" placeholder="结束日期" aria-label="结束日期" readonly v-bind="attrs" v-on="on" dense clearable outlined hide-details /></template>
              <v-date-picker v-model="query.end" @input="menu2=false" no-title scrollable locale="zh-cn" />
            </v-menu>
          </div></div>
          <div><label for="student-status-filter">学员状态</label><v-select id="student-status-filter" :items="queryStatusItems" v-model="query.status" item-text="name" item-value="id" aria-label="学员状态" dense outlined hide-details /></div>
          <div><label for="student-class-filter">分班情况</label><v-select id="student-class-filter" :items="joinClassItems" v-model="query.isJoinClass" item-text="name" item-value="id" aria-label="分班情况" dense outlined hide-details /></div>
        </div>
        <div class="student-filter-bottom"><span><v-icon size="14">mdi-shield-check-outline</v-icon>当前校区内的学员档案</span><div><v-btn text small class="student-reset" :disabled="query.loading" @click="resetQuery"><v-icon left size="15">mdi-refresh</v-icon>重置</v-btn><v-btn depressed small class="student-query" type="submit" :loading="query.loading"><v-icon left size="16">mdi-magnify</v-icon>查询学员</v-btn></div></div>
      </form>
    </section>

    <section class="student-list-panel" aria-label="学员列表" :aria-busy="query.loading">
      <div class="student-list-toolbar"><div><h2>学员列表</h2><span class="student-count"><b>{{data.total}}</b> 位学员</span></div><v-btn outlined small class="student-export" :disabled="query.loading" @click="exportExcel"><v-icon left size="16">mdi-tray-arrow-down</v-icon>导出</v-btn></div>
      <v-progress-linear v-if="query.loading" indeterminate color="#d9363e" height="2" aria-label="正在加载学员" />
      <div class="student-table-scroll" tabindex="0" aria-label="学员列表，可横向滚动">
        <table class="student-table">
          <colgroup><col class="student-col-identity"><col class="student-col-gender"><col class="student-col-source"><col class="student-col-contact"><col class="student-col-class"><col class="student-col-status"><col class="student-col-parent"><col class="student-col-date"><col class="student-col-action"></colgroup>
          <thead><tr><th scope="col">学员信息</th><th scope="col">性别 / 年龄</th><th scope="col">生源类型</th><th scope="col">联系人信息</th><th scope="col">分班情况</th><th scope="col">学员状态</th><th scope="col">家长登录手机</th><th scope="col">创建时间</th><th scope="col">操作</th></tr></thead>
          <tbody>
            <tr v-for="(item,index) in data.list" :key="item.id">
              <td><div class="student-identity"><button type="button" class="student-avatar-button" :aria-label="'查看' + item.studentName + '的详情'" @click="viewStudentDetail(item)"><v-avatar size="38" rounded :class="'student-avatar-tone-' + (index % 5)"><v-img v-if="item.avatar" :src="imgPrev + item.avatar + imgSize50" :alt="item.studentName" /><span v-else>{{(item.studentName || '学').slice(0,1)}}</span></v-avatar></button><div class="student-identity-text"><button type="button" class="student-name" @click="viewStudentDetail(item)">{{item.studentName}}</button><div class="student-number">{{item.studentNo}}</div></div></div></td>
              <td><div class="student-gender">{{genderName(item.gender)}}</div><div class="student-age">{{item.age === null || item.age === undefined || item.age === '' ? '—' : item.age}}</div></td>
              <td>{{item.sourceName || '—'}}</td>
              <td><div class="student-contact-role">{{item.contactorCall || '—'}}</div><div class="student-phone">{{item.contactorMobile || '—'}}</div></td>
              <td><span :class="{'student-unassigned': item.classCount == 0, 'student-assigned': item.classCount != 0}">{{joinClassName(item.classCount)}}<template v-if="item.classCount > 0"> · {{item.classCount}} 班</template></span></td>
              <td><span class="student-status" :class="{'student-status-current':item.status === '1'}">{{studentStatusName(item.status)}}</span></td>
              <td><template v-if="item.appUserMobile"><div class="student-phone">{{item.appUserMobile}}</div><div class="student-bound"><v-icon size="12">mdi-shield-check-outline</v-icon>已绑定</div></template><button v-else type="button" class="student-bind" @click="showParent(item)">去绑定<v-icon size="13">mdi-chevron-right</v-icon></button></td>
              <td><span class="student-created">{{item.createAt || '—'}}</span></td>
              <td><button type="button" class="student-detail-action" @click="viewStudentDetail(item)">学员详情</button></td>
            </tr>
            <tr v-if="!data.list.length"><td colspan="9" class="student-empty" role="status">{{query.loading ? '正在加载学员，请稍候…' : '暂无符合条件的学员，请调整筛选条件'}}</td></tr>
          </tbody>
        </table>
      </div>
      <div class="student-pagination"><div class="student-page-total"><span>共 <strong>{{data.total}}</strong> 项</span><v-select :items="[10,20,50,100]" @change="handleSizeChange" dense hide-details aria-label="每页数量" label="每页数量" outlined v-model="query.size" /></div><v-pagination v-if="Math.ceil(data.total/query.size)>0" :length="Math.ceil(data.total/query.size)" @input="handleCurrentChange" :total-visible="5" v-model="query.page" color="#d9363e" /></div>
    </section>

    <studentDetail :show="studentDetail.dialog" :studentId="studentDetail.studentId" @cancel="studentDetailCanceled" />
    <bindParentMobile :show="bindParent.show" :studentId="bindParent.studentId" :studentName="bindParent.studentName" @cancel="bindParent.show=false" @success="bindParentSuccess" />
    <createStudent :show="add.dialog" @cancel="add.dialog=false" @success="createStudentSuccess" />
    <importStudent :show="importStudent.show" @cancel="importStudent.show=false" @success="importStudentSuccess" @toResult="toResultPage" />
  </div>
</template>

<script>
	import * as XLSX from "xlsx";
	import global from '../../../config/global'
	import utils from '../../../config/utils'
	import studentDetail from "../../../components/student_detail.vue"
	import bindParentMobile from "../../../components/bind_parent_mobile.vue"
	import createStudent from "../../../components/create_student.vue"
	import importStudent from "../../../components/import_student.vue"
	export default {
		name: "student-list",
		components:{studentDetail,bindParentMobile,createStudent,importStudent},
		data() {
			return {
				menu1: false, // 开始时间菜单
				menu2: false, // 结束时间菜单
				currentShopId:"",
				breadcrumbs: [{
						text: '首页',
						disabled: false,
						href: "/"
					},
					{
						text: '教务中心',
						disabled: true
					},
					{
						text: '学员管理',
						disabled: true
					},
				],
				imgPrev: global.config.imgPre,
				imgSize50:global.config.imgSize50,
				data: {
					total: 0,
					headers: [
						{
							text: '学号',
							value: 'studentNo',
							sortable: false
						},
						{
							text: '姓名',
							value: 'studentName',
							sortable: false
						},
						{
							text: '头像',
							value: 'avatar',
							sortable: false
						},
						{
							text: '生源类型',
							value: 'sourceName',
							sortable: false
						},
						{
							text: '性别',
							value: 'gender',
							sortable: false
						},
						{
							text: '年龄',
							value: 'age',
							sortable: false
						},
						{
							text: '联系人信息',
							value: 'contactorInfo',
							sortable: false
						},

						{
							text: '是否分班',
							value: 'classCount',
							sortable: false
						},
						{
							text: '状态',
							value: 'status',
							sortable: false
						},
						{
							text: '家长登录手机',
							value: 'appUserMobile',
							sortable: false
						},
						{
							text: '创建时间',
							value: 'createAt',
							sortable: false
						},
						{
							text: '操作',
							align: 'center',
							value: 'action',
							sortable: false
						},
					],
					list: [],
				},
				search: "",
				selectShopList: [], //选择门店数据源
				joinClassItems:[{id: "",name: "全部"},{id: "1",name: "已分班"},{id: "0",name: "未分班"}],
				queryStatusItems: [{
					id: "",
					name: "全部"
				}, {
					id: "1",
					name: "正式学员"
				}, {
					id: "2",
					name: "结业学员"
				}, {
					id: "3",
					name: "意向学员"
				}, {
					id: "4",
					name: "无意向"
				}],
				statusItems: [{
					id: "1",
					name: "正式学员"
				}, {
					id: "3",
					name: "意向学员"
				}],
				query: {
					loading: false,
					page: 1,
					size: 20,
					start: "",
					end: "",
					studentName: "",
					studentNo: "",
					status: "1",
					isJoinClass:"",
					mobile:""
				},

				add: {
					dialog: false,
				},
				bindParent: {
					show: false,
					studentId: "",
					studentName: "",
				},
				studentDetail:{
					dialog:false,
					studentId:""
				},
				importStudent:{
					show:false
				}

			}
		},
		created() {
			// 设置当前校区ID
			this.currentShopId = utils.getCurrentShopId();

			// 接收传入的是否加入班级参数
			let isJoinClass = this.$route.query.isJoinClass;
			if (isJoinClass){
				this.query.isJoinClass = isJoinClass;
			}

			console.log("isJoinClass",isJoinClass);


			this.getTableDataList();
		},
		methods: {
            resetQuery() {
                Object.assign(this.query, {page:1, start:'', end:'', studentName:'', studentNo:'', mobile:'', status:'1', isJoinClass:''});
                this.menu1 = false;
                this.menu2 = false;
                this.getTableDataList();
            },
			queryClicked(){
				this.query.page = 1;
				this.getTableDataList();
			},
			handleSizeChange(size) {
				this.query.size = size;
				this.query.page = 1;
				this.getTableDataList();
			},
			handleCurrentChange(page) {
				this.query.page = page;
				this.getTableDataList();
			},
			showParent(item) {
				this.bindParent.show = true;
				this.bindParent.studentId = item.id;
				this.bindParent.studentName = item.studentName;
			},
			createStudentSuccess(){
				this.add.dialog=false;
				this.getTableDataList();
			},
			importStudentSuccess(){
				this.importStudent.show = false;
				this.getTableDataList();
			},
			showAddDialog() {
				this.add.dialog = true;
			},
			exportExcel() {
				const that = this;
				this.query.loading = true;

				let url = `/edu/student/export?v=1`;
				if (this.currentShopId) {
					url += "&shopId=" +this.currentShopId;
				}

				if (this.query.start) {
					url += "&start=" + this.query.start;
				}
				if (this.query.end) {
					url += "&end=" + this.query.end;
				}
				if (this.query.studentName) {
					url += "&studentName=" + this.query.studentName;
				}
				if (this.query.studentNo) {
					url += "&studentNo=" + this.query.studentNo;
				}
				if (this.query.mobile) {
					url += "&mobile=" + this.query.mobile;
				}

				if (this.query.status) {
					url += "&status=" + this.query.status;
				}
				if (this.query.isJoinClass) {
					url += "&isJoinClass=" + this.query.isJoinClass;
				}


				this.axios.get(url).then(response => {

					console.log(response);

					if (response.data["code"] === 200) {
						let exportArr = response.data["data"] ? response.data["data"] : [];

						const fdArray = [];
						exportArr.forEach(function(data, index) {
							var obj = {
								序号: index + 1,
								学号: data.studentNo,
								姓名: data.studentName,
								年龄:  data.age,
								性别: data.genderName,
								生源类型:data.sourceName,
								联系人称呼:data.contactorCall,
								联系人手机: data.contactorMobile,
								就读学校: data.fromSchoolName,
								居住片区: data.fromRegionName,
								家庭住址: data.homeAddress
							};
							fdArray.push(obj);
						});
						// 新建一个excel.xlsx
						var wb = XLSX.utils.book_new();
						//封装JSON 数据
						var fdXslxws = XLSX.utils.json_to_sheet(fdArray);
						XLSX.utils.book_append_sheet(wb, fdXslxws, "sheet");
						XLSX.writeFile(wb, "学员导出列表" + ".xlsx");

						that.query.loading = false;

						that.$noty.success("导出成功");
					}else {
						that.query.loading = false;
						that.$noty.error("获取失败：" + response.data["message"]);
					}

				})
			},
			getTableDataList() {
				const that = this;
				this.query.loading = true;

				let url = `/edu/student/list/page/${that.query.page - 1}/size/${that.query.size}?v=1`;
				if (this.currentShopId) {
					url += "&shopId=" +this.currentShopId;
				}

				if (this.query.start) {
					url += "&start=" + this.query.start;
				}
				if (this.query.end) {
					url += "&end=" + this.query.end;
				}
				if (this.query.studentName) {
					url += "&studentName=" + this.query.studentName;
				}
				if (this.query.studentNo) {
					url += "&studentNo=" + this.query.studentNo;
				}
				if (this.query.mobile) {
					url += "&mobile=" + this.query.mobile;
				}

				if (this.query.status) {
					url += "&status=" + this.query.status;
				}
				if (this.query.isJoinClass) {
					url += "&isJoinClass=" + this.query.isJoinClass;
				}

				console.log(url);

				this.axios.get(url).then(response => {

					console.log(response);

					if (response.data["code"] === 200) {
						that.data.list = response.data["data"]["list"] ? response.data["data"]["list"] : [];
						that.data.total = response.data["data"]["count"];

						that.query.loading = false;
						return
					}else if (response.data["code"] === 7031){
						// 调整到登录页
						that.$router.push('/login');
					}
					that.query.loading = false;
					that.$noty.error("获取失败：" + response.data["message"]);
				})
			},
			genderName(state) {

				let stName = "未知";
				if (state === "1") {
					stName = "男生";
				} else if (state === "0") {
					stName = "女生";
				}
				return stName;
			},
			genderColor(state) {
				let color = "";
				if (state === "1") {
					color = "blue lighten-4";
				} else if (state === "0") {
					color = "pink lighten-4";
				}

				return color;
			},
			joinClassColor(classCount){
				const count = parseInt(classCount);
				let color = "";
				if (count > 0) {
					color = "success";
				} else if (count === 0) {
					color = "error";
				}
				return color;
			},
			joinClassName(classCount){
				const count = parseInt(classCount);
				let stName = "未知";
				if (count > 0) {
					stName = "已分班";
				} else if (count === 0) {
					stName = "未分班";
				}
				return stName;
			},
			studentStatusName(state) {

				let stName = "未知";
				if (state === "1") {
					stName = "在读";
				} else if (state === "0") {
					stName = "冻结";
				} else if (state === "2") {
					stName = "结业";
				} else if (state === "3") {
					stName = "意向";
				} else if (state === "4") {
					stName = "无意向";
				}
				return stName;
			},
			studentStatusColor(state) {
				let color = "";
				if (state === "1") {
					color = "success";
				} else if (state === "0") {
					color = "error";
				} else if (state === "2") {
					color = "primary";
				} else if (state === "3") {
					color = "warning";
				} else if (state === "4") {
					color = "error";
				}

				return color;
			},
			viewStudentDetail(item){
				// 显示用户详情
				console.log(item);
				this.studentDetail.dialog = true;
				this.studentDetail.studentId = item.id;
			},
			studentDetailCanceled(){
				this.studentDetail.dialog = false;
				//this.getTableDataList();
			},
			bindParentSuccess(){
				this.bindParent.show=false;
				this.getTableDataList();
			},
			toResultPage(e){
				console.log("e",e);
				// this.$router.push({
				// 	path: `/edu/import/student/result/${this.shopId}/${e.batchNo}`
				// })

				this.$router.push(`/edu/import/student/result/${e.batchNo}`);
			}
		}
	}
</script>

<style scoped lang="scss">
@import "@/assets/styles/student-management.scss";
</style>
